
import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery, Box, CircularProgress, IconButton, Popover,Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TablePagination from '@mui/material/TablePagination';
import api from '../api';
import SearchInput from '../customcomponents/CustomSearchInput';
import { Search as SearchIcon } from '@mui/icons-material';


const formatTimeDifference = (firstResponse, ticketOpen) => {
  const ticketOpenTime = new Date(ticketOpen);
  const firstResponseTime = new Date(firstResponse);
  const timeDifference = firstResponseTime.getTime() - ticketOpenTime.getTime();
  const seconds = Math.floor(timeDifference / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  let timeDifferenceString = '';
  if (days > 0) {
    timeDifferenceString += `${days}d `;
  }else if (hours > 0) {
    timeDifferenceString += `${hours}h `;
  }else if (minutes > 0) {
    timeDifferenceString += `${minutes}m `;
  }else {
    timeDifferenceString += `${seconds}s`;
  }
  return timeDifferenceString;
};

export default function TATReportPage() {

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    
    const handleChangePage = (event, newPage) => {
    setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleSearchClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose=(e)=>{
      setAnchorEl(null);
      // setSearchQuery('');
    }

    useEffect(() => {
      // fetch tickets for the current page and rowsPerPage
      const fetchTatReportData = async () => {
        setLoading(true);
        setError(null);
        try {
          // Backend tickets endpoint in backend is exposed via ticketController.getAllTickets
          // In this frontend project the api base is configured in src/api.js so call /tickets
          const response = await api.get('/tat', {
            params: {
              page: page + 1,
              limit: rowsPerPage
            }
          });
  
          // response.data expected shape: { success: true, data: [...], pagination: { total } }
          const data = response.data;
          if (data) {
            // If the backend returns success wrapper
            if (data.success && Array.isArray(data.data)) {
              setRows(data.data);
              setTotalRows(data.pagination?.total || data.data.length);
              return data.data;
            } else if (Array.isArray(data)) {
              // fallback if API returns array directly
              setRows(data);
              setTotalRows(data.length);
              return data;
            } else {
              setRows([]);
              setTotalRows(0);
            }
          }
        } catch (e) {
          console.error('Failed to fetch tickets for TAT report', e);
          setError('Failed to load report data');
          setRows([]);
          setTotalRows(0);
        } finally {
          setLoading(false);
        }
      };

      fetchTatReportData();
    }, [page, rowsPerPage]);


    const searchQueryData = useMemo(() => {
      if(searchQuery){
        return rows.filter((row) => {
          return row.TTRM_Patient_Ref?.TA_Patient_Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          row.TTRM_Patient_Ref?.TA_Patient_Mobile_Number?.includes(searchQuery)
        })
      }
      return rows;
    }, [rows,searchQuery]);

  return (
    <Box sx={{  background: 'background.default' }}>
      
      <Paper elevation={0} sx={{
        p: { xs: 2, sm: 3 },
        mb: 2,
        borderRadius: 3,
        background: theme.palette.mode == 'dark' ? '#32373a' :  'white',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: { xs: 'row', md: 'row' },
        alignItems: 'center',
        gap: 2,
        justifyContent: 'space-between',
        position: 'sticky',
        top: isSmallScreen?10:0,
        zIndex: 1000,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,flexDirection: { xs: 'row', sm: 'row' } }}>
          {
            isSmallScreen ? (
              <Typography variant="h6" sx={{ fontWeight: 700, flex: 1, textTransform: 'capitalize', color: 'background.text',  }}>TAT Report</Typography>
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 700, flex: 1, textTransform: 'capitalize', color: 'background.text',  }}>TAT Report</Typography>
            )
          }
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2,flexDirection: { xs: 'row', sm: 'row' } }}>
          {!isSmallScreen && (<SearchInput value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />)}
            {isSmallScreen && 
              <>
                <IconButton 
                  onClick={handleSearchClick}
                  color='primary'
                  // sx={{
                  //   bgcolor: theme.palette.primary.main,
                  //   color: theme.palette.primary.contrastText,
                  //   borderRadius: 2, boxShadow: 1,
                  //   '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 2, },
                  //   height: 40, width: 40,
                  // }}
                >
                  <SearchIcon />
                </IconButton>
                {/* <IconButton
                  onClick={() => setCreateDialog(true)}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    borderRadius: 2, boxShadow: 1, height: 40, width: 40,
                    '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 2,}, 
                  }}
                >
                  <AddIcon />
                </IconButton> */}

                <Popover open={open} anchorEl={anchorEl} onClose={handleClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left', }}
                  transformOrigin={{  vertical: 'top',  horizontal: 'left', }}
                  PaperProps={{ sx: { p: 1, boxShadow: 'none', backgroundColor: 'rgba(255, 255, 255, 0.2)', borderRadius: 2, width: '60vw',  maxWidth: 400, }, }}
                >
                  <SearchInput value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}  autoFocus />  
                </Popover>  
              </>
            } 
        </Box>
      </Paper>

      {
        isSmallScreen ? (
          <>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2, mt: 2 }}>
          {loading ? (
            <Box
              sx={{ m:1,mt:0,p: 2, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', background: theme.palette.mode === 'dark' ? '#32373a' : 'white', }} >
              {Array.from(new Array(10)).map((_, idx) => (
                <Box key={idx}
                  sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', alignItems: 'center', mt: idx === 0 ? 0 : 1, }} >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'background.text' }}>
                    <Skeleton variant="text" width={80} height={20} />
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: 'background.text' }}>
                    <Skeleton variant="text" width={120} height={20} />
                  </Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between', alignItems: 'center', mt: 1, }} >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'background.text' }}>
                  <Skeleton variant="text" width={100} height={20} />
                </Typography>
                <Skeleton variant="rectangular" width={100} height={30} sx={{ borderRadius: 2 }} />
              </Box>
            </Box>

          ) : error ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ color: 'error.main' }}>
                {error}
              </TableCell>
            </TableRow>
          ) : searchQueryData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{color:theme.palette.background.text}}>No records found</TableCell>
            </TableRow>
          ) : (
            searchQueryData.map((row, idx) => {
              const sl = page * rowsPerPage + idx + 1;
              const name = row.patient?.name || row.TTRM_Patient_Ref?.TA_Patient_Name || '—';
              const mobile = row.patient?.mobile || row.TTRM_Patient_Ref?.TA_Patient_Mobile_Number || '—';
              const ticketOpen = row.TTRM_Ticket_Open ? new Date(row.TTRM_Ticket_Open).toLocaleString() : '—';
              const firstSource = row.TTRM_First_Source || '—';
              const firstResponseIn = row.TTRM_Fisrt_Response ? formatTimeDifference(row.TTRM_Fisrt_Response, ticketOpen) : '—';
              const proName = row.patient?.TA_PRO_Ref?.name || row.TTRM_Patient_Ref?.TA_PRO_Ref?.name || '—';
              const amount = Number(row.TTRM_Payment_Total || 0).toFixed(2) || '0.00';
              const closed = row.TTRM_Ticket_Close ? new Date(row.TTRM_Ticket_Close).toLocaleString() : '—';
              let   tat =  row.TTRM_Ticket_TAT || '—';

              return (
                <Box key={row._id || idx} sx={{
                  p: 2,
                  borderRadius: 3,
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                  background: theme.palette.mode == 'dark' ? '#32373a' : 'white',
                  m:1,mt:0
                }}>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'background.text',fontSize: '0.8rem' }}>SL No.</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{sl}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1, color: 'background.text',fontSize: '0.8rem' }}>Name</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{name}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1, color: 'background.text', fontSize: '0.8rem'}}>Mobile</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{mobile}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>First Source</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}><Box sx={{width:'100px',height:'30px',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor:"#2e7d32",p:1,borderRadius:5,color:'#fff'}}>{firstSource}</Box></Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>First Response In</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{firstResponseIn}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>Ticket Open</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{ticketOpen}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>Ticket Close</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', }}>{closed}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>TAT</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{tat}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>Amount</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{amount}</Typography>
                  </Box>
                  <Box sx={{display:'flex',flexDirection:'row',width:'100%',justifyContent:'space-between',alignItems:'center'}}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt:1,  color: 'background.text', fontSize: '0.8rem'}}>PRO Name</Typography>
                    <Typography  sx={{ fontSize:'12px', color: 'background.text', fontSize: '0.8rem'}}>{proName}</Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

          </>
        ) : (
          <Paper elevation={1} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', p: 0, overflow: 'auto', mb: 3, }}>
            <TableContainer sx={{ maxHeight: {xs: 400, sm: 500, md: 600,} }}>
              <Table stickyHeader sx={{backgroundColor:'background.paper'}}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>SL No.</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>Mobile</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>First Source</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>First Response In</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>Ticket Open</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>Ticket Close</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>TAT</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '1rem', background: '#0088cc', color: '#fff' }}>PRO Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow sx={{ '&:hover': { backgroundColor: theme.palette.action.hover }, transition: 'background 0.2s', }}>
                      {/* SL No. */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={30} height={20} /> </TableCell>
                      {/* Name */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={100} height={20} /> </TableCell>
                      {/* Mobile */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={100} height={20} /> </TableCell>
                      {/* First Source (badge) */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="rectangular" width={100} height={30} /> </TableCell>
                      {/* First Response In */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={60} height={20} /> </TableCell>
                      {/* Ticket Open */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={40} height={20} /> </TableCell>
                      {/* Ticket Close */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={40} height={20} /> </TableCell>
                      {/* TAT */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={50} height={20} /> </TableCell>
                      {/* Amount */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={60} height={20} /> </TableCell>
                      {/* PRO Name */}
                      <TableCell sx={{ color: 'background.text' }}> <Skeleton variant="text" width={80} height={20} /> </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ color: 'error.main' }}>
                        {error}
                      </TableCell>
                    </TableRow>
                  ) : searchQueryData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{color:theme.palette.background.text}}>No records found</TableCell>
                    </TableRow>
                  ) : (
                    searchQueryData.map((row, idx) => {
                      
                      const sl = page * rowsPerPage + idx + 1;
                      const name = row.patient?.name || row.TTRM_Patient_Ref?.TA_Patient_Name || '—';
                      const mobile = row.patient?.mobile || row.TTRM_Patient_Ref?.TA_Patient_Mobile_Number || '—';
                      const ticketOpen = row.TTRM_Ticket_Open ? new Date(row.TTRM_Ticket_Open).toLocaleString() : '—';
                      const firstSource = row.TTRM_First_Source || '—';
                      const firstResponseIn = row.TTRM_Fisrt_Response ? formatTimeDifference(row.TTRM_Fisrt_Response, ticketOpen) : '—';
                      const proName = row.patient?.TA_PRO_Ref?.name || row.TTRM_Patient_Ref?.TA_PRO_Ref?.name || '—';
                      const amount = Number(row.TTRM_Payment_Total || 0).toFixed(2) || '0.00';
                      const closed = row.TTRM_Ticket_Close ? new Date(row.TTRM_Ticket_Close).toLocaleString() : '—';
                      let   tat =  row.TTRM_Ticket_TAT || '—';
                      // const tatTime = formatTAT_TimeDifference(ticketOpen, tat);

                      return (
                        <TableRow key={row._id || idx} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover }, transition: 'background 0.2s' }}>
                          <TableCell sx={{color:'background.text'}}>{sl}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{name}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{mobile}</TableCell>
                          <TableCell sx={{color:'background.text'}}><Box sx={{width:'100px',height:'30px',display:'flex',alignItems:'center',justifyContent:'center',backgroundColor:"#2e7d32",p:1,borderRadius:5,color:'#fff'}}>{firstSource}</Box></TableCell>
                          <TableCell sx={{color:'background.text'}}>{firstResponseIn}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{ticketOpen}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{closed}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{tat}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{amount}</TableCell>
                          <TableCell sx={{color:'background.text'}}>{proName}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>


            <Box sx={{ px: 2, py: 1, background: theme.palette.mode === 'dark' ? 'transparent':'#fff' }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    // marginBottom: 0,
                  },
                  background: theme.palette.mode === 'dark' ? 'transparent':'#fff',
                  color: 'background.text',
                }}
              />
            </Box>
          </Paper>

        )
      }

    </Box>
  );
}

